import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth, homeFor } from "@/context/AuthContext";

const links = [["/", "Home"], ["/tutors", "Find Tutors"], ["/about", "About"]];

function Nav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B1A33]/85 text-white backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} end data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[#3CC34A]" : "text-slate-300 hover:text-white"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          {user ? (
            <Link to={homeFor(user)} data-testid="nav-dashboard-button" className="rounded-full bg-[#1F6FFF] px-5 py-2 text-sm font-semibold transition-colors hover:bg-[#1450C9]">Dashboard</Link>
          ) : (
            <>
              <Link to="/login/student" data-testid="nav-login-button" className="text-sm font-semibold text-slate-200 hover:text-white">Log in</Link>
              <Link to="/register/student" data-testid="nav-register-button" className="rounded-full bg-[#3CC34A] px-5 py-2 text-sm font-bold text-[#0B1A33] transition-colors hover:bg-[#2DA638]">Get started</Link>
            </>
          )}
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="flex flex-col gap-4 border-t border-white/10 px-6 py-5 md:hidden" onClick={() => setOpen(false)}>
          {links.map(([to, label]) => <Link key={to} to={to} className="text-slate-200">{label}</Link>)}
          <Link to="/login/student" className="text-slate-200">Student login</Link>
          <Link to="/login/tutor" className="text-slate-200">Tutor login</Link>
          <Link to="/register/student" className="rounded-full bg-[#3CC34A] px-5 py-2 text-center font-bold text-[#0B1A33]">Get started</Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#071120] text-slate-400">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4 lg:px-12">
        <div className="md:col-span-2">
          <Logo className="h-14" testid="footer-logo-link" />
          <p className="mt-4 max-w-sm text-sm">Live, credit-backed coaching with verified tutors. Explore to Excel™.</p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white">Students</p>
          <Link to="/register/student" data-testid="footer-student-register">Register</Link>
          <Link to="/login/student" data-testid="footer-student-login">Student login</Link>
          <Link to="/tutors">Find tutors</Link>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white">Tutors & Admin</p>
          <Link to="/register/tutor" data-testid="footer-tutor-register">Apply as tutor</Link>
          <Link to="/login/tutor" data-testid="footer-tutor-login">Tutor login</Link>
          <Link to="/admin/login" data-testid="footer-admin-login">Admin login</Link>
        </div>
      </div>
      <div className="border-t border-white/5 py-5 text-center text-xs">© {new Date().getFullYear()} FosterApp™. All rights reserved.</div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <Nav />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
